import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { Link } from "react-router-dom";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import MpgInput from "../../../../../CommonFormComp/MpgInput";
import MpgFileUpload from "../../../../../fileHandling/MpgFileUpload";

import ClientTeamDialog from "../../ClientTeamDialog";
import AccountAdminsDialog from "../../AccountAdminsDialog";
import AccountTeamMemberDialog from "../../AccountTeamMemberDialog";

import { useViewAccountTeamMembersListQuery } from "../../../../../reactQueries/AppApi/rqHooksForAccountTeamMembers";
import MpgAutocompleteSingle from "../../../../../CommonFormComp/MpgAutocompleteSingle";
import MpgAutocompleteMulti from "../../../../../CommonFormComp/MpgAutocompleteMulti";
import {
  useGetClientCodeListQuery,
  useFileDownloadMutation,
} from "../../../../../reactQueries/commonRQHooks";
import {
  useUploadLogoOfClientAccountMutation,
  useUpdateClientAccountByIdMutation,
  useDeleteClientAccountByIdMutation,
} from "../../../../../reactQueries/AppApi/rqHooksForClientAccount";
import { useGetClientUserListByClientAccountIdQuery } from "../../../../../reactQueries/AppApi/rqHooksForClientUsers";
import CircularProgress from "@mui/material/CircularProgress";
import ViewAllClientUserForNotiDialog from "../../viewAllUser/ViewAllClientUserForNotiDialog";
import { editClientAccValidation } from "./editClientAccValidation";
import ClientTeamTable from "../../accountTableComp/ClientTeamTable";
import AccountTeamTable from "../../accountTableComp/AccountTeamTable";
import AccountAdminTable from "../../accountTableComp/AccountAdminTable";
import Stack from "@mui/material/Stack";
import { clientAccountViewPath } from "../../../../../routes/routePaths";
import MpgErrorDialog from "../../../../../common/dialog/MpgErrorDialog";
import MpgSuccessDialog from "../../../../../common/dialog/MpgSuccessDialog";
import MpgLoadingDialog from "../../../../../common/dialog/MpgLoadingDialog";
import { saveAs } from "file-saver";
import MuiLink from "@mui/material/Link";
import MpgConfirmDialogForAccAndSubAccount from "../../../../../common/dialog/MpgConfirmDialogForAccAndSubAccount";
import useTrackUserActivity from "../../../../../userActivityTracking/useTrackUserActivity";
import { getGUIDAndExtentionFromFileURL } from "../../../../../hooksAndFunctions/functions/getGUIDAndExtentionFromFileURL";
import { useGetAccountAndSubAccListOnlyForA4Role } from "../../../../../hooksAndFunctions/customHooks/useGetAccountAndSubAccListOnlyForA4Role";
import { getAccessTypes } from "../../../../../hooksAndFunctions/functions/getAccessTypes";
import { useGetLoggedInUserDetails } from "../../../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import DeleteIcon from "@mui/icons-material/Delete";
import MpgClientCodeTransferListDialogForClientAcc from "../../../../../CommonFormComp/MpgClientCodeTransferListForClientAcc/MpgClientCodeTransferListDialogForClientAcc";

import {
  mapLangListToAutocomplete,
  mapCurrencyListToAutocomplete,
  mapClientTypeListToAutocomplete,
  setClientCodes,
} from "../../../../../common/utilities/Helper";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const EditClientAccount = ({
  clientAccountDataById,
  LangList,
  CurrencyList,
  ClientTypeList,
  accountAdminListFromAPI,
}) => {
  const { refClientAccountCmsData } = useOutletContext();
  const { id: clientAccountId } = useParams();

  let navigate = useNavigate();

  const userName = JSON.parse(window.sessionStorage.getItem("UserAccount"))?.[
    "name"
  ];

  const { loggedInUser } = useGetLoggedInUserDetails();
  const { id: userId } = loggedInUser;

  const { clientPortalAdminType } = getAccessTypes();
  const { hasA4Access } = clientPortalAdminType;

  const { schema } = editClientAccValidation();

  const rhfFormInstance = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      client_acc_name: clientAccountDataById?.ClientAccountName,
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const { setValue, watch } = rhfFormInstance;

  const selectedPrimaryLang = watch("primary_lang");

  const [clientTeamTableData, setClientTeamTableData] = useState([]);
  const [accountTeamMemberTableData, setAccountTeamMemberTableData] = useState([]);
  const [accountAdminTableData, setAccountAdminTableData] = useState([]);
  const [wasAdminEdited, setWasAdminEdited] = useState(false);

  const [openClientTeamDialog, setOpenClientTeamDialog] = useState(false);
  const [openAccountTeamDialog, setOpenAccountTeamDialog] = useState(false);
  const [openAccountAdminDialog, setOpenAccountAdminDialog] = useState(false);
  const [openClientAccCodeDialog, setOpenClientAccCodeDialog] = useState(false);
  const [openAllClientUserNotiDialog, setOpenAllClientUserNotiDialog] = useState(false);

  const {
    mutate: downloadLogo,
    isError: isErrorOnDownload,
    isSuccess: isSuccessOnDownload,
    data: dataOnDownload,
  } = useFileDownloadMutation();

  const {
    isSuccess: isSuccessOnClientCodeList,
    data: ClientCodeListData,
    isFetching: isFetchingOnClientCodeList,
  } = useGetClientCodeListQuery();

  const {
    mutate: updateClientAccount,
    isError: isErrorOnUpdateCA,
    isLoading: isLoadingOnUpdateCA,
    isSuccess: isSuccessOnUpdateCA,
    error: errorOnUpdateCA,
  } = useUpdateClientAccountByIdMutation();

  const {
    mutate: uploadLogo,
    isError: isErrorOnLogoUpload,
    isLoading: isLoadingOnLogoUpload,
    isSuccess: isSuccessOnLogoUpload,
    data: dataOnLogoUpload,
  } = useUploadLogoOfClientAccountMutation();

  const {
    mutate: deleteClientAccountById,
    isError: isErrorOnDeleteCA,
    isLoading: isLoadingOnDeleteCA,
    isSuccess: isSuccessOnDeleteCA,
    variables: variablesOnDeleteCA,
  } = useDeleteClientAccountByIdMutation();

  const { data: accountTeamMembersList } = useViewAccountTeamMembersListQuery();

  const {
    isSuccess: isSuccessOnGetClientUsers,
    data: dataOnGetClientUsers,
    refetch: refetchOnGetClientUsers,
  } = useGetClientUserListByClientAccountIdQuery({ clientAccountId });

  const primaryLangAutocompleteList = mapLangListToAutocomplete(LangList);
  const currencyAutocompleteList = mapCurrencyListToAutocomplete(CurrencyList);
  const ClientTypeAutocompleteList = mapClientTypeListToAutocomplete(ClientTypeList);

  const [supportiveLangAutocompleteList, setSupportiveLangAutocompleteList] = useState([]);

  useEffect(() => {
    if (selectedPrimaryLang?.id) {
      setSupportiveLangAutocompleteList(
        mapLangListToAutocomplete(
          LangList?.filter((item) => item.Id !== selectedPrimaryLang.id)
        )
      );
    }
  }, [selectedPrimaryLang, LangList]);

  // ✅ Populate Created By and Created Date fields
  useEffect(() => {
    setValue("created_by", clientAccountDataById?.CreatedBy || "");
    setValue(
      "created_date",
      clientAccountDataById?.CreatedTimestamp
        ? new Date(clientAccountDataById.CreatedTimestamp).toLocaleDateString(
            "en-GB",
            {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            }
          )
        : ""
    );
  }, [setValue, clientAccountDataById]);

  useEffect(() => {
    rhfFormInstance.setValue("accountAdmins", accountAdminTableData, {
      shouldValidate: true,
      shouldTouch: true,
    });
    rhfFormInstance.trigger("accountAdmins");
  }, [accountAdminTableData]);

  const clientAccLangList = clientAccountDataById?.AccountLanguages;
  const [existingPrimLang, setExistingPrimLang] = useState({});
  const [existingSupportedLang, setExistingSupportedLang] = useState([]);

  useEffect(() => {
    if (!!clientAccLangList && !!clientAccLangList.length) {
      let prim = {};
      let supportive = [];
      clientAccLangList.forEach((eachLang) => {
        const foundLang =
          !!LangList && LangList.find((item) => item.Id === eachLang.LanguageID);
        if (!!foundLang) {
          if (!!eachLang.IsPrimary) {
            prim = foundLang;
          } else {
            supportive.push(foundLang);
          }
        }
      });
      setExistingPrimLang(prim);
      setExistingSupportedLang(supportive);
    } else {
      setExistingPrimLang({});
      setExistingSupportedLang([]);
    }
  }, [clientAccLangList, LangList]);

  useEffect(() => {
    if (!!existingPrimLang.Id) {
      const existingPrimLangDefaultList = {
        id: existingPrimLang.Id,
        selectOptionLabel: `${existingPrimLang.LanguageCode}-${existingPrimLang.LanguageDescription}`,
        selectOptionValue: existingPrimLang.Id,
      };
      setValue("primary_lang", existingPrimLangDefaultList || "", {
        shouldValidate: true,
      });
    }
  }, [existingPrimLang]);

  useEffect(() => {
    if (!!existingSupportedLang.length) {
      const existingSupportedLangList =
        !!existingSupportedLang &&
        existingSupportedLang.map((item) => {
          return {
            id: item.Id,
            selectOptionLabel: `${item.LanguageCode}-${item.LanguageDescription}`,
            selectOptionValue: item.Id,
          };
        });
      setValue("supported_lang", existingSupportedLangList || [], {
        shouldValidate: true,
      });
    }
  }, [existingSupportedLang]);

  const [filteredAdmnList, setFilteredAdmnList] = useState([]);
  useEffect(() => {
    if (!!accountAdminListFromAPI && !!accountAdminListFromAPI.length) {
      let newList = [];
      const regExp = /A1|A2|A4|Enterprise Admin/;
      accountAdminListFromAPI.forEach((item) => {
        if (item?.permissionRole?.search(regExp) !== -1) {
          newList.push(item);
        }
      });
      setFilteredAdmnList(newList);
    }
  }, [accountAdminListFromAPI]);

  const [finalCodeList, setFinalCodeList] = useState([]);
  useEffect(() => {
    if (!!clientAccountDataById?.ClientAccountCodes) {
      const existingCodeList = clientAccountDataById?.ClientAccountCodes.map(
        (item) => ({
          clientCode: item.clientCode,
          clientCodeName: item.clientCodeName,
        })
      );
      setFinalCodeList(existingCodeList);
    }
  }, [clientAccountDataById?.ClientAccountCodes]);

  useEffect(() => {
    setClientCodes(finalCodeList, setValue);
  }, [finalCodeList, setValue]);

  useEffect(() => {
    if (!!clientAccountDataById?.CurrencyId && !!CurrencyList) {
      const existingCurrencyDefaultList =
        !!CurrencyList &&
        CurrencyList.filter(
          (item) => item.Id == clientAccountDataById?.CurrencyId
        ).map((item) => ({
          id: item.Id,
          selectOptionLabel: `${item.CurrencyCode}`,
          selectOptionValue: item.Id,
        }));
      setValue("CurrencyId", existingCurrencyDefaultList[0] || "", {
        shouldValidate: true,
      });
    } else {
      setValue("CurrencyId", "");
    }
  }, [clientAccountDataById?.CurrencyId, CurrencyList]);

  useEffect(() => {
    if (clientAccountDataById?.DataClassificationKey && ClientTypeList) {
      const existingClientTypeDefaultList = ClientTypeList.filter(
        (item) =>
          item.DataClassificationKey ==
          clientAccountDataById.DataClassificationKey
      ).map((item) => ({
        id: item.DataClassificationKey,
        selectOptionLabel: `${item.MasterDataClassificationName}`,
        selectOptionValue: item.DataClassificationKey,
      }));
      setValue("ClientType", existingClientTypeDefaultList[0] || "", {
        shouldValidate: true,
      });
    } else {
      setValue("ClientType", "");
    }
  }, [clientAccountDataById?.DataClassificationKey, ClientTypeList]);

  const [filteredCL1CL2ClientUsers, setFilteredCL1CL2ClientUsers] = useState([]);
  useEffect(() => {
    if (!!isSuccessOnGetClientUsers && !!dataOnGetClientUsers) {
      let newList = [];
      const regExp = /CL1|CL2|CL9/;
      dataOnGetClientUsers.forEach((item) => {
        if (item?.PermissionCode.search(regExp) !== -1) {
          newList.push(item);
        }
      });
      setFilteredCL1CL2ClientUsers(newList);
    }
  }, [dataOnGetClientUsers, isSuccessOnGetClientUsers]);

  useEffect(() => {
    if (!!clientAccountDataById) {
      const existingClientUsers = clientAccountDataById?.ClientUserTeams;
      if (!!existingClientUsers) {
        let newList = [];
        const regExp = /CL1|CL2|CL9/;
        existingClientUsers?.forEach((item) => {
          if (item?.PermissionCode.search(regExp) !== -1) {
            newList.push(item);
          }
        });
        setClientTeamTableData(newList || []);
      }

      const existingAccTeamMemberList = clientAccountDataById?.AccountUserTeams;
      if (!!existingAccTeamMemberList) {
        let list = [];
        existingAccTeamMemberList &&
          existingAccTeamMemberList.forEach((item) => {
            list.push({
              teamMemberId: item.StaffId,
              name: `${item.FirstName} ${item.LastName}`,
              email: item.EmailAddress,
              contact: item.ContactNo,
              jobPosition: item.JobPosition,
              bioId: item.BioId,
              bioTitle: item.TemplateTitle,
            });
          });
        setAccountTeamMemberTableData(list);
      }
    }
  }, [clientAccountDataById]);

  useEffect(() => {
    const existingClientadmins = clientAccountDataById?.ClientAccountAdmins;
    if (!!existingClientadmins) {
      let requiredAdminList = [];
      existingClientadmins.forEach((eachAdmin) => {
        const foundItem =
          !!accountAdminListFromAPI &&
          accountAdminListFromAPI.find(
            (item) => item.adminId === eachAdmin.adminId
          );
        if (!!foundItem) {
          requiredAdminList.push(foundItem);
        }
      });
      setAccountAdminTableData(requiredAdminList);
    }
  }, [clientAccountDataById, accountAdminListFromAPI]);

  const [removeLogo, setRemoveLogo] = useState(false);

  const [bodyDataForUpdate, setBodyDataForUpdate] = useState({});
  const onSubmit = (data) => {
    const primLang = {
      IsPrimary: true,
      LanguageID: data.primary_lang?.id,
    };
    const supportedLangList =
      !!data.supported_lang &&
      data.supported_lang.map((item) => ({
        IsPrimary: false,
        LanguageID: item?.id,
      }));

    const langList = [...supportedLangList, primLang];

    const codeList =
      !!finalCodeList &&
      finalCodeList.map((item) => ({
        ClientCode: item.clientCode,
        ClientCodeName: item.clientCodeName,
      }));

    let clientUserList = [];
    if (!!clientTeamTableData) {
      const list = clientTeamTableData.map((item, index) => ({
        Id: item.Id,
        Orders: index + 1,
      }));
      clientUserList = !!list ? list : [];
    }

    let accountTeamMembList = [];
    if (!!accountTeamMemberTableData) {
      const list = accountTeamMemberTableData.map((item, index) => ({
        StaffId: item.teamMemberId,
        BioId: item.bioId,
        Orders: index + 1,
      }));
      accountTeamMembList = !!list ? list : [];
    }

    let clientAccAdminList = [];
    if (!!accountAdminTableData) {
      const list = accountAdminTableData.map((item) => ({
        adminId: item.adminId,
      }));
      clientAccAdminList = !!list ? list : [];
    }

    const body = {
      Id: clientAccountId,
      ClientAccountName: data.client_acc_name || "",
      IsActive: true,
      UpdatedBy: userName || "",
      CurrencyId: data?.CurrencyId?.id,
      DataClassificationKey: data.ClientType?.id,
      AccountLanguages: langList || [],
      ClientUserTeams: clientUserList || [],
      AccountUserTeams: accountTeamMembList || [],
      ClientAccountAdmins: clientAccAdminList || [],
      ClientAccountCodes: codeList || [],
      Logo: !!removeLogo ? "" : clientAccountDataById?.Logo,
    };
    setBodyDataForUpdate(body);

    if (!!data.logo_img[0]) {
      const file = data.logo_img[0];
      uploadLogo({ file });
    } else {
      updateClientAccount({ body, clientAccountId });
    }
  };

  useEffect(() => {
    if (!!isSuccessOnLogoUpload) {
      const storedFileURL = dataOnLogoUpload.data.FileUrl;
      const bodyData = {
        ...bodyDataForUpdate,
        Logo: storedFileURL,
      };
      updateClientAccount({ body: bodyData, clientAccountId });
    }
  }, [isSuccessOnLogoUpload]);

  useEffect(() => {
    if (!!isSuccessOnUpdateCA || !!isSuccessOnDeleteCA) {
      navigate(clientAccountViewPath);
    }
  }, [isSuccessOnUpdateCA, isSuccessOnDeleteCA]);

  const [emailNotiSuccessFromViewDialog, setEmailNotiSuccessFromViewDialog] = useState(false);
  const [emailNotiErrorFromViewDialog, setEmailNotiErrorFromViewDialog] = useState(false);
  const [emailNotiLoadingFromViewDialog, setEmailNotiLoadingFromViewDialog] = useState(false);
  const [userActivationSuccessFromViewDialog, setUserActivationSuccessFromViewDialog] = useState(false);
  const [userActivationErrorFromViewDialog, setUserActivationErrorFromViewDialog] = useState(false);
  const [userActivationLoadingFromViewDialog, setUserActivationLoadingFromViewDialog] = useState(false);

  const [fileDetails, setFileDetails] = useState({});
  useEffect(() => {
    if (!!clientAccountDataById?.Logo) {
      const file = clientAccountDataById?.Logo;
      const splitFileURL = file.split("/");
      const fileGUIDWithExtension = splitFileURL.at(-1);
      const fileGUIDWithExtensionSplit = fileGUIDWithExtension.split(".");
      const fileGUID = fileGUIDWithExtensionSplit[0];
      const fileExtension = fileGUIDWithExtensionSplit[1];
      setFileDetails({ fileGUID, fileExtension });
    } else {
      setFileDetails({});
    }
  }, [clientAccountDataById?.Logo]);

  useEffect(() => {
    if (!!isSuccessOnDownload) {
      saveAs(dataOnDownload, `${clientAccountDataById?.ClientAccountName}_logo`);
    }
  }, [isSuccessOnDownload]);

  const { clientAccountIdListForA4Role } =
    useGetAccountAndSubAccListOnlyForA4Role({ userId });

  const [disabledUpdateButton, setDisabledUpdateButton] = useState(false);
  useEffect(() => {
    if (
      !!hasA4Access &&
      !clientAccountIdListForA4Role.includes(clientAccountDataById?.Id)
    ) {
      setDisabledUpdateButton(true);
    } else {
      if (!clientAccountDataById?.IsActive) {
        setDisabledUpdateButton(true);
      } else {
        setDisabledUpdateButton(false);
      }
    }
  }, [hasA4Access, clientAccountIdListForA4Role, clientAccountDataById]);

  const [disabledDeactivateReactivateButton, setDisabledDeactivateReactivateButton] = useState(false);
  useEffect(() => {
    if (
      !!hasA4Access &&
      !clientAccountIdListForA4Role.includes(clientAccountDataById?.Id)
    ) {
      setDisabledDeactivateReactivateButton(true);
    } else {
      setDisabledDeactivateReactivateButton(false);
    }
  }, [hasA4Access, clientAccountIdListForA4Role]);

  const [openMpgConfirmDialogForAccAndSubAccount, setOpenMpgConfirmDialogForAccAndSubAccount] = useState(false);

  const { trackActivity } = useTrackUserActivity();
  useEffect(() => {
    if (!!isSuccessOnDownload) {
      trackActivity({
        activitySuccess: !!isSuccessOnDownload,
        actionType: "Downloaded",
        itemId: fileDetails?.fileGUID || "",
      });
    }
    if (!!isSuccessOnDeleteCA) {
      let actionType = "";
      if (!!variablesOnDeleteCA?.isDeleteMode) {
        actionType = "Deactivated";
      } else if (!variablesOnDeleteCA?.isDeleteMode) {
        actionType = "Activated";
      }
      trackActivity({
        activitySuccess: !!isSuccessOnDeleteCA,
        actionType: actionType,
        itemId: clientAccountId || "",
      });
    }
    if (!!isSuccessOnLogoUpload) {
      const { fileGUID } = getGUIDAndExtentionFromFileURL({
        fileURL: dataOnLogoUpload?.data?.FileUrl,
      });
      trackActivity({
        activitySuccess: !!isSuccessOnLogoUpload,
        actionType: "Uploaded",
        itemId: fileGUID || "",
      });
    }
  }, [isSuccessOnDownload, isSuccessOnDeleteCA, variablesOnDeleteCA, clientAccountId, isSuccessOnLogoUpload]);

  const fetchClientAccountCmsData = (key) => {
    if (
      refClientAccountCmsData?.Components &&
      refClientAccountCmsData?.Components["ClientAccountDetails.SetupClientAccount"]
    ) {
      const adminClientSubAccountCmsDataLabel =
        refClientAccountCmsData?.Components["ClientAccountDetails.SetupClientAccount"];
      return returnLabel(adminClientSubAccountCmsDataLabel?.Labels, key);
    }
    return "";
  };

  const fetchClientTeamMemberCmsData = (key) => {
    if (
      refClientAccountCmsData?.Components &&
      refClientAccountCmsData?.Components["ClientAccounts.ClientTeamMembers"]
    ) {
      const adminClientSubAccountCmsDataLabel =
        refClientAccountCmsData?.Components["ClientAccounts.ClientTeamMembers"];
      return returnLabel(adminClientSubAccountCmsDataLabel?.Labels, key);
    }
    return "";
  };

  const fetchAccountTeamMemberCmsData = (key) => {
    if (
      refClientAccountCmsData?.Components &&
      refClientAccountCmsData?.Components["ClientAccounts.AccountTeamMembers"]
    ) {
      const adminClientSubAccountCmsDataLabel =
        refClientAccountCmsData?.Components["ClientAccounts.AccountTeamMembers"];
      return returnLabel(adminClientSubAccountCmsDataLabel?.Labels, key);
    }
    return "";
  };

  const fetchAccountAdmnCmsData = (key) => {
    if (
      refClientAccountCmsData?.Components &&
      refClientAccountCmsData?.Components["ClientAccounts.AccountAdmins"]
    ) {
      const adminClientSubAccountCmsDataLabel =
        refClientAccountCmsData?.Components["ClientAccounts.AccountAdmins"];
      return returnLabel(adminClientSubAccountCmsDataLabel?.Labels, key);
    }
    return "";
  };

  const returnLabel = (labelArray = [], key) => {
    return labelArray.find((x) => x.Key === key)?.Phrase || "";
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ width: "100%" }}>
        <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
          {fetchClientAccountCmsData("ClientAccountDetails.EditClientAccounts")}
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          m={1}
        >
          <Button
            variant="outlined"
            size="small"
            component={Link}
            to={clientAccountViewPath}
          >
            {fetchClientAccountCmsData("ClientAccountDetails.ClientAccounts")}
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={() => setOpenAllClientUserNotiDialog(true)}
          >
            {fetchClientAccountCmsData("ClientAccountDetails.ViewAll")}
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={() => setOpenMpgConfirmDialogForAccAndSubAccount(true)}
            disabled={!!disabledDeactivateReactivateButton}
          >
            {!!clientAccountDataById?.IsActive
              ? fetchClientAccountCmsData("ClientAccountDetails.Deactivate")
              : fetchClientAccountCmsData("ClientAccountDetails.Reactivate")}
          </Button>
        </Stack>

        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={rhfFormInstance.handleSubmit(onSubmit)}
        >
          <Item>
            <Typography variant="h6" align="left" sx={{ fontWeight: "bolder" }}>
              {fetchClientAccountCmsData("ClientAccountDetails.ClientAccountDetails")}
            </Typography>
            <Grid container spacing={1}>
              {/* Left column */}
              <Grid item xs={12} sm={6}>
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"client_acc_name"}
                  label={fetchClientAccountCmsData("ClientAccountDetails.ClientAccountName")}
                  fullWidth
                />
                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"primary_lang"}
                  label={fetchClientAccountCmsData("ClientAccountDetails.PrimaryLanguages")}
                  fullWidth
                  optionList={primaryLangAutocompleteList || []}
                />
                <MpgAutocompleteMulti
                  rhfFormInstance={rhfFormInstance}
                  name={"supported_lang"}
                  label={fetchClientAccountCmsData("ClientAccountDetails.SupportedLanguages")}
                  fullWidth
                  optionList={supportiveLangAutocompleteList || []}
                  disabled={!selectedPrimaryLang?.id}
                />
                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"CurrencyId"}
                  label={fetchClientAccountCmsData("ClientAccountDetails.Currency")}
                  fullWidth
                  optionList={currencyAutocompleteList || []}
                />
                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"ClientType"}
                  label={fetchClientAccountCmsData("ClientAccountDetails.Type")}
                  fullWidth
                  optionList={ClientTypeAutocompleteList || []}
                />
                <Box>
                  <MpgFileUpload
                    name={"logo_img"}
                    label={fetchClientAccountCmsData("ClientAccountDetails.Logo")}
                    rhfFormInstance={rhfFormInstance}
                  />
                  {!removeLogo && !!clientAccountDataById?.Logo && (
                    <Grid>
                      <Tooltip title={fetchClientAccountCmsData("ClientAccountDetails.RemoveLogo")}>
                        <IconButton
                          aria-label="delete"
                          onClick={() => setRemoveLogo(true)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <MuiLink
                        onClick={() => {
                          if (!!fileDetails?.fileGUID) {
                            downloadLogo({
                              fileGUID: fileDetails?.fileGUID,
                              DownloadType: "logo",
                              Extension: fileDetails?.fileExtension,
                            });
                          }
                        }}
                      >
                        {`${clientAccountDataById?.ClientAccountName}_logo`}
                      </MuiLink>
                    </Grid>
                  )}
                </Box>
              </Grid>

              {/* ✅ Right column — no duplicate, Created By & Created Date clearly shown */}
              <Grid item xs={12} sm={6}>
                <div>
                  <MpgInput
                    rhfFormInstance={rhfFormInstance}
                    label={fetchClientAccountCmsData("ClientAccountDetails.ClientCodes")}
                    name={"client_codes"}
                    multiline
                    rows={8}
                    fullWidth
                    disabled={true}
                  />
                  {!!isFetchingOnClientCodeList && (
                    <Button disabled>
                      <CircularProgress size={15} sx={{ margin: "5px" }} />
                      {fetchClientAccountCmsData("ClientAccountDetails.Fetch")}
                    </Button>
                  )}
                  {!!isSuccessOnClientCodeList && (
                    <Button onClick={() => setOpenClientAccCodeDialog(true)}>
                      {fetchClientAccountCmsData("ClientAccountDetails.SelectClientCodes")}
                    </Button>
                  )}
                </div>

                {/* ✅ Created By */}
                <Box sx={{ mt: 2 }}>
                  <MpgInput
                    rhfFormInstance={rhfFormInstance}
                    name={"created_by"}
                    label={fetchClientAccountCmsData("ClientAccountDetails.CreatedBy")}
                    fullWidth
                    disabled={true}
                  />
                </Box>

                {/* ✅ Created Date */}
                <Box sx={{ mt: 2 }}>
                  <MpgInput
                    rhfFormInstance={rhfFormInstance}
                    name={"created_date"}
                    label={fetchClientAccountCmsData("ClientAccountDetails.CreatedDate")}
                    fullWidth
                    disabled={true}
                  />
                </Box>
              </Grid>
            </Grid>
          </Item>

          <br />
          <Item>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6" align="center" sx={{ fontWeight: "bolder" }}>
                {refClientAccountCmsData?.Components &&
                  refClientAccountCmsData?.Components["ClientAccounts.ClientTeamMembers"]?.Title}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setOpenClientTeamDialog(true)}
              >
                {fetchClientTeamMemberCmsData("ClientTeamMembers.Select")}
              </Button>
            </Stack>
            <ClientTeamTable
              tableData={clientTeamTableData || []}
              setTableData={setClientTeamTableData}
            />
          </Item>

          <br />
          <Item>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6" align="center" sx={{ fontWeight: "bolder" }}>
                {refClientAccountCmsData?.Components &&
                  refClientAccountCmsData?.Components["ClientAccounts.AccountTeamMembers"]?.Title}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setOpenAccountTeamDialog(true)}
              >
                {fetchAccountTeamMemberCmsData("AccountTeamMembers.Select")}
              </Button>
            </Stack>
            <AccountTeamTable
              tableData={accountTeamMemberTableData}
              setTableData={setAccountTeamMemberTableData}
            />
          </Item>

          <br />
          <Item
            sx={{
              border:
                rhfFormInstance.formState.errors.accountAdmins &&
                (rhfFormInstance.formState.isSubmitted || wasAdminEdited)
                  ? "1px solid red"
                  : undefined,
              borderRadius: 1,
              pb: 1,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6" align="center" sx={{ fontWeight: "bolder" }}>
                {refClientAccountCmsData?.Components &&
                  refClientAccountCmsData?.Components["ClientAccounts.AccountAdmins"]?.Title}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setOpenAccountAdminDialog(true)}
              >
                {fetchAccountAdmnCmsData("AccountAdmins.Select")}
              </Button>
            </Stack>
            <AccountAdminTable
              tableData={accountAdminTableData}
              setTableData={setAccountAdminTableData}
              rhfFormInstance={rhfFormInstance}
              setWasAdminEdited={setWasAdminEdited}
            />
            {rhfFormInstance.formState.errors.accountAdmins &&
              (rhfFormInstance.formState.isSubmitted || wasAdminEdited) && (
                <Typography color="error" variant="caption" align="left" sx={{ px: 2, pt: 1 }}>
                  {rhfFormInstance.formState.errors.accountAdmins.message}
                </Typography>
              )}
          </Item>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent={"space-evenly"}
            mt={3}
          >
            <Button
              variant="contained"
              size="small"
              type="submit"
              sx={{ marginTop: "15px" }}
              disabled={!!disabledUpdateButton}
            >
              {fetchClientAccountCmsData("ClientAccountDetails.UpdateClientAccount")}
            </Button>
          </Stack>
        </Box>

        <ClientTeamDialog
          open={openClientTeamDialog}
          setOpen={setOpenClientTeamDialog}
          clientTeamTableData={clientTeamTableData || []}
          setClientTeamTableData={setClientTeamTableData}
          clientUserListFromAPI={filteredCL1CL2ClientUsers || []}
        />
        <AccountTeamMemberDialog
          open={openAccountTeamDialog}
          setOpen={setOpenAccountTeamDialog}
          accountTeamMemberTableData={accountTeamMemberTableData || []}
          setAccountTeamMemberTableData={setAccountTeamMemberTableData}
          accountTeamMembersListFromAPI={accountTeamMembersList || []}
        />
        <AccountAdminsDialog
          open={openAccountAdminDialog}
          setOpen={setOpenAccountAdminDialog}
          accountAdminTableData={accountAdminTableData || []}
          setAccountAdminTableData={setAccountAdminTableData}
          accountAdminListFromAPI={filteredAdmnList || []}
        />
      </Box>

      <MpgClientCodeTransferListDialogForClientAcc
        transferDialogTitle={fetchClientAccountCmsData("ClientAccountDetails.SelectClientCodes")}
        leftTableTitle={fetchClientAccountCmsData("ClientAccountDetails.AvailableClientCodes")}
        rightTableTitle={fetchClientAccountCmsData("ClientAccountDetails.SelectedClientCodes")}
        open={openClientAccCodeDialog}
        setOpen={setOpenClientAccCodeDialog}
        grabRightTableData={setFinalCodeList}
        leftTableDataToPop={ClientCodeListData || []}
        rightTableDataToPop={finalCodeList || []}
        clientAccountDataById={clientAccountDataById || {}}
        whereUsed={"Edit"}
      />

      <ViewAllClientUserForNotiDialog
        open={openAllClientUserNotiDialog}
        setOpen={setOpenAllClientUserNotiDialog}
        clientAccOrSubAccName={clientAccountDataById?.ClientAccountName || ""}
        clientUserListFromAPI={filteredCL1CL2ClientUsers || []}
        refetchListFunc={refetchOnGetClientUsers}
        setEmailNotiSuccessFromViewDialog={setEmailNotiSuccessFromViewDialog}
        setEmailNotiErrorFromViewDialog={setEmailNotiErrorFromViewDialog}
        setEmailNotiLoadingFromViewDialog={setEmailNotiLoadingFromViewDialog}
        setUserActivationSuccessFromViewDialog={setUserActivationSuccessFromViewDialog}
        setUserActivationErrorFromViewDialog={setUserActivationErrorFromViewDialog}
        setUserActivationLoadingFromViewDialog={setUserActivationLoadingFromViewDialog}
        isDeactivated={!clientAccountDataById?.IsActive}
      />

      <MpgErrorDialog
        open={
          !!isErrorOnUpdateCA ||
          !!isErrorOnLogoUpload ||
          !!isErrorOnDeleteCA ||
          !!emailNotiErrorFromViewDialog ||
          !!userActivationErrorFromViewDialog ||
          !!isErrorOnDownload
        }
        title={`${
          !!isErrorOnUpdateCA &&
          errorOnUpdateCA?.response?.data === "AccountNameExists"
            ? fetchClientAccountCmsData("ClientAccountDetails.ValidationMsgAccountNameExists")
            : ""
        }`}
        content={`${
          !!isErrorOnUpdateCA &&
          errorOnUpdateCA?.response?.data === "AccountNameExists"
            ? fetchClientAccountCmsData(
                "ClientAccountDetails.ValidationMsgDuplicateAccountName"
              ).replace("#CLIENT_ACCOUNT_NAME", `${watch("client_acc_name")}`)
            : ""
        }`}
      />

      <MpgSuccessDialog
        open={!!emailNotiSuccessFromViewDialog || !!userActivationSuccessFromViewDialog}
      />
      <MpgLoadingDialog
        open={
          !!emailNotiLoadingFromViewDialog ||
          !!userActivationLoadingFromViewDialog ||
          !!isLoadingOnUpdateCA ||
          !!isLoadingOnLogoUpload ||
          !!isLoadingOnDeleteCA
        }
      />

      {!!clientAccountDataById?.IsActive && (
        <MpgConfirmDialogForAccAndSubAccount
          open={openMpgConfirmDialogForAccAndSubAccount}
          setOpen={setOpenMpgConfirmDialogForAccAndSubAccount}
          actionType="DeactivateAccount"
          handlerOnDeactivation={() => {
            const id = clientAccountDataById?.Id;
            const isActive = clientAccountDataById?.IsActive;
            if (!!isActive) {
              deleteClientAccountById({
                clientAccountId: id,
                isDeleteMode: true,
                isClientAccount: true,
                isActivateAll: false,
              });
            }
          }}
        />
      )}
      {!clientAccountDataById?.IsActive && (
        <MpgConfirmDialogForAccAndSubAccount
          open={openMpgConfirmDialogForAccAndSubAccount}
          setOpen={setOpenMpgConfirmDialogForAccAndSubAccount}
          actionType="ActivateAccount"
          handlerOnAccountOnlyActivation={() => {
            const id = clientAccountDataById?.Id;
            const isActive = clientAccountDataById?.IsActive;
            if (!isActive) {
              deleteClientAccountById({
                clientAccountId: id,
                isDeleteMode: false,
                isClientAccount: true,
                isActivateAll: false,
              });
            }
          }}
          handlerOnAllActivation={() => {
            const id = clientAccountDataById?.Id;
            const isActive = clientAccountDataById?.IsActive;
            if (!isActive) {
              deleteClientAccountById({
                clientAccountId: id,
                isDeleteMode: false,
                isClientAccount: true,
                isActivateAll: true,
              });
            }
          }}
        />
      )}
    </Box>
  );
};

export default EditClientAccount;